import React, { useState, useEffect, useRef } from 'react';
import { getNews, adminCreateNews, adminUpdateNews, adminDeleteNews, adminUploadNewsImage } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Newspaper, Upload, ImageIcon, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', excerpt: '', is_published: true });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);

  const fetchData = async () => {
    try { const res = await getNews(); setNews(res.data); }
    catch (error) { toast.error('Fehler'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API}/api/files/${path}`;
  };

  const handleSave = async () => {
    try {
      let newsId;
      if (editing) {
        await adminUpdateNews(editing.news_id, form);
        newsId = editing.news_id;
        toast.success('Aktualisiert');
      } else {
        const res = await adminCreateNews(form);
        newsId = res.data.news_id;
        toast.success('Erstellt');
      }
      // Upload image if selected
      if (imageFile && newsId) {
        const fd = new FormData();
        fd.append('file', imageFile);
        await adminUploadNewsImage(newsId, fd);
      }
      setDialogOpen(false);
      setEditing(null);
      setImageFile(null);
      setForm({ title: '', content: '', excerpt: '', is_published: true });
      fetchData();
    } catch (error) { toast.error('Fehler'); }
  };

  const handleDelete = async (n) => {
    if (!window.confirm(`"${n.title}" löschen?`)) return;
    try { await adminDeleteNews(n.news_id); toast.success('Gelöscht'); fetchData(); }
    catch (error) { toast.error('Fehler'); }
  };

  const handleImageUploadExisting = async (newsId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(newsId);
      try {
        const fd = new FormData();
        fd.append('file', file);
        await adminUploadNewsImage(newsId, fd);
        toast.success('Bild hochgeladen');
        fetchData();
      } catch (err) { toast.error('Upload fehlgeschlagen'); }
      finally { setUploading(null); }
    };
    input.click();
  };

  return (
    <div data-testid="admin-news-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-bold uppercase">News</h1>
        <Button onClick={() => { setEditing(null); setImageFile(null); setForm({ title: '', content: '', excerpt: '', is_published: true }); setDialogOpen(true); }} className="btn-primary" data-testid="add-news-btn">
          <Plus className="w-4 h-4 mr-2" />News
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">{[1, 2].map(i => <Card key={i} className="card-surface animate-pulse"><CardContent className="p-6"><div className="h-16 bg-white/10 rounded" /></CardContent></Card>)}</div>
      ) : news.length > 0 ? (
        <div className="space-y-4">
          {news.map(n => (
            <Card key={n.news_id} className="card-surface">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {n.image_url ? (
                    <img src={getPhotoUrl(n.image_url)} alt="" className="w-16 h-16 object-cover rounded-sm" />
                  ) : (
                    <div className="w-16 h-16 bg-white/5 rounded-sm flex items-center justify-center">
                      <Newspaper className="w-6 h-6 text-text-secondary" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-heading font-bold uppercase">{n.title}</h3>
                      <Badge className={n.is_published ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                        {n.is_published ? 'Veröffentlicht' : 'Entwurf'}
                      </Badge>
                    </div>
                    <p className="text-text-secondary text-sm">{formatDate(n.created_at)}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" className="text-accent" onClick={() => handleImageUploadExisting(n.news_id)} disabled={uploading === n.news_id} title="Bild hochladen">
                    {uploading === n.news_id ? <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" /> : <Upload className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(n); setImageFile(null); setForm({ title: n.title, content: n.content, excerpt: n.excerpt || '', is_published: n.is_published }); setDialogOpen(true); }}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(n)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="card-surface"><CardContent className="p-8 text-center"><Newspaper className="w-12 h-12 text-text-secondary mx-auto mb-4" /><p className="text-text-secondary">Keine News.</p></CardContent></Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader><DialogTitle>{editing ? 'News bearbeiten' : 'Neue News'}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2"><Label>Titel *</Label><Input className="input-dark" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="news-title-input" /></div>
            <div className="space-y-2"><Label>Kurztext</Label><Input className="input-dark" value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} /></div>
            <div className="space-y-2"><Label>Inhalt *</Label><Textarea className="input-dark min-h-[150px]" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} /></div>
            
            {/* Image Upload */}
            <div className="space-y-2">
              <Label>Bild / Flyer</Label>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => setImageFile(e.target.files?.[0] || null)} />
              {imageFile ? (
                <div className="relative">
                  <img src={URL.createObjectURL(imageFile)} alt="" className="w-full h-40 object-cover rounded-sm" />
                  <button onClick={() => { setImageFile(null); if (fileRef.current) fileRef.current.value = ''; }} className="absolute top-2 right-2 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center">
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ) : editing?.image_url ? (
                <img src={getPhotoUrl(editing.image_url)} alt="" className="w-full h-40 object-cover rounded-sm" />
              ) : null}
              <Button type="button" variant="outline" className="w-full border-dashed border-white/20 text-text-secondary" onClick={() => fileRef.current?.click()}>
                <ImageIcon className="w-4 h-4 mr-2" />
                {imageFile ? 'Anderes Bild wählen' : 'Bild hochladen'}
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
              <Label>Veröffentlicht</Label>
            </div>
            <Button onClick={handleSave} className="btn-primary w-full">{editing ? 'Aktualisieren' : 'Erstellen'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminNews;
